import React, { useState } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useUser } from "../context/UserProvider";
import { auth } from "../lib/firebase";
import logo from "../assets/naim-logo.png";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, changeLanguage, getCurrentLanguage } from "../i18n";

export default function ForceSignInWarning() {
  const { forceSignIn } = useUser();
  const { t } = useTranslation();
  const [isForcing, setIsForcing] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(getCurrentLanguage());

  const handleForceSignIn = async () => {
    setIsForcing(true);
    try {
      await forceSignIn();
    } catch (err) {
      console.error("Failed to force sign in:", err);
      alert(t('auth.failedToSignIn'));
    } finally {
      setIsForcing(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 relative">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4">
        <button
          onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
          className="flex items-center gap-2 bg-white px-3 py-2 rounded-full border border-gray-200 shadow-sm hover:shadow-md transition"
        >
          <Globe size={16} className="text-naimGreen" />
          <span className="text-naimGreen font-medium text-sm">
            {SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.nativeName?.slice(0, 8) || 'EN'}
          </span>
          <ChevronDown size={14} className="text-naimGreen" />
        </button>
        {showLanguageDropdown && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 z-50 max-h-80 overflow-y-auto">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setSelectedLanguage(lang.code);
                  changeLanguage(lang.code);
                  setShowLanguageDropdown(false);
                }}
                className={`w-full px-4 py-2 text-left flex justify-between items-center hover:bg-green-50 transition ${lang.code === selectedLanguage ? 'bg-green-50 text-naimGreen' : 'text-gray-700'}`}
              >
                <span>{lang.nativeName}</span>
                {lang.code === selectedLanguage && <Check size={16} className="text-naimGreen" />}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-full max-w-lg bg-white rounded-2xl shadow-md border border-gray-200 p-6">
        <img src={logo} alt="Naim" className="h-12 mx-auto mb-6" />

        <div className="mb-6">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-center mb-2">{t('auth.accountActiveOnAnotherDevice')}</h1>
          <p className="text-sm text-gray-600 text-center">
            {t('auth.forceSignOutWarning')}
          </p>
          <p className="text-sm text-gray-600 text-center mt-2">
            {t('auth.forceSignOutPlatforms')}
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-amber-800">
            ⚠️ <strong>{t('auth.forceSignOutImportant')}</strong>
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleForceSignIn}
            disabled={isForcing}
            className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-lg bg-naimGreen text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isForcing ? t('auth.signingIn') : t('auth.yesSignInThisDevice')}
          </button>
          <button
            onClick={() => {
              auth.signOut().catch(() => { });
            }}
            disabled={isForcing}
            className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 font-medium"
          >
            {t('auth.cancelAndSignOut')}
          </button>
        </div>
      </div>
    </div>
  );
}
